import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionPanel,
} from '@americanexpress/dls-react';
import { cn } from '@/helpers/utils';
import { getCurrentDayForTimeZone } from '@/helpers/dateTimeHelper';
import {
  useLoungeById,
  useLoungeHoursInLocaleFormat,
  useLoungeName,
} from '@/hooks/loungesHooks/loungesHelperHooks';
import { useRouteParams } from '@/context/routeParamsContext';
import { generateIdentifier } from '@/helpers/analyticsHelpers';
import LoungeOpenStatusText from '../common/LoungeOpenStatusText';
import styles from './LoungeDetails.module.scss';

const LoungeDetailsOpeningHours = () => {
  const { airportCode, product, loungeId, pathname } = useRouteParams();
  const identifier = generateIdentifier({
    airportCode,
    product,
    loungeId,
    pathname,
  });
  const loungeName = useLoungeName(loungeId);
  const localeLoungeHours = useLoungeHoursInLocaleFormat(loungeId);
  const lounge = useLoungeById(loungeId);
  const [currentDay, setCurrentDay] = React.useState('');

  React.useEffect(() => {
    setCurrentDay(getCurrentDayForTimeZone('en-US', lounge.airport.time_zone));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load
  }, []);

  if (Array.isArray(localeLoungeHours)) {
    return (
      <div
        id="lounge-hours-accordion"
        className="margin-3-b tw-border-b tw-border-gray-300"
      >
        <Accordion isBordered={false} className={cn('-tw-mx-4')}>
          <AccordionPanel
            heading={<LoungeOpenStatusText />}
            id="Accordion-hours-of-operation"
            reactlytics={{
              identifier,
              element: 'hours of operation accordion',
              meta: {
                info: `${loungeName} at ${airportCode}`,
              },
            }}
            className={styles.hours}
          />
          <AccordionContent
            aria-labelledby="Accordion-hours-of-operation"
            className="pad-3-t pad-l tw-mb-5"
          >
            <dl>
              {localeLoungeHours.map(
                ({
                  day,
                  dayTranslated,
                  firstOpenTime,
                  firstCloseTime,
                  secondOpenTime,
                  secondCloseTime,
                }) => {
                  const hoursClass =
                    currentDay === day ? 'tw-font-bold' : 'tw-font-normal';
                  return (
                    <div key={day} className={cn('flex')}>
                      <dt
                        className={cn(
                          'tw-break-keep tw-capitalize',
                          hoursClass
                        )}
                      >
                        {dayTranslated}
                      </dt>
                      <dd
                        className={cn(
                          'tw-mb-2 tw-w-full tw-text-right',
                          hoursClass
                        )}
                      >
                        <div>
                          {firstOpenTime}
                          <span className={styles.separator}> - </span>
                          {firstCloseTime}
                        </div>
                        {secondOpenTime && secondCloseTime && (
                          <div>
                            {secondOpenTime}
                            <span className={styles.separator}> - </span>
                            {secondCloseTime}
                          </div>
                        )}
                      </dd>
                    </div>
                  );
                }
              )}
            </dl>
          </AccordionContent>
        </Accordion>
      </div>
    );
  }
  return (
    <div className="margin-3-b pad-3-tb tw-border-b tw-border-gray-300">
      <h2 className="heading-3 margin-1-b color-text-emphasis">Hours</h2>
      <p className="margin-0-l">{localeLoungeHours}</p>
    </div>
  );
};

export default LoungeDetailsOpeningHours;
